import { Shield, Zap, Globe2, ShieldCheck, Server, Lock } from 'lucide-react';

const features = [
  {
    titleKey: 'home.feature1_title',
    descKey: 'home.feature1_desc',
    Icon: Shield,
    stat: 'AES-256',
    statLabel: 'Encryption',
    accentClass: 'text-primary-600',
    bgClass: 'bg-primary-50',
    borderClass: 'border-primary-100',
    iconBg: 'bg-primary-600',
  },
  {
    titleKey: 'home.feature2_title',
    descKey: 'home.feature2_desc',
    Icon: Zap,
    stat: '< 50ms',
    statLabel: 'Delivery',
    accentClass: 'text-amber-600',
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-100',
    iconBg: 'bg-amber-500',
  },
  {
    titleKey: 'home.feature3_title',
    descKey: 'home.feature3_desc',
    Icon: Globe2,
    stat: 'AZ·EN·RU',
    statLabel: 'Languages',
    accentClass: 'text-emerald-600',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-100',
    iconBg: 'bg-emerald-600',
  },
  {
    titleKey: 'home.feature4_title',
    descKey: 'home.feature4_desc',
    Icon: ShieldCheck,
    stat: '99.8%',
    statLabel: 'Accuracy',
    accentClass: 'text-rose-600',
    bgClass: 'bg-rose-50',
    borderClass: 'border-rose-100',
    iconBg: 'bg-rose-600',
  },
];

export default function FeaturesSection() {
  const { t } = useTranslation();
  return (
    <section className="py-24 bg-white" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <span className="inline-block bg-primary-50 text-primary-600 text-xs font-semibold px-3 py-1 rounded-full tracking-wide uppercase mb-4 border border-primary-100">
            {t('home.features_badge')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mb-4">{t('home.features_title')}</h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">{t('home.features_subtitle')}</p>
        </div>

        <div className="space-y-16">
          {features.map((f, i) => (
            <div key={i} className={`flex flex-col lg:flex-row items-center gap-12 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Text */}
              <div className="flex-1">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${f.iconBg} mb-6 shadow-lg`}>
                  <f.Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{t(f.titleKey)}</h3>
                <p className="text-slate-500 text-lg leading-relaxed mb-6">{t(f.descKey)}</p>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-400 text-sm">emailaze.com datacenter</span>
                </div>
              </div>

              {/* Visual card */}
              <div className="flex-1 flex justify-center">
                <div className={`w-full max-w-sm rounded-3xl p-8 border-2 ${f.borderClass} ${f.bgClass}`}>
                  {/* Server-like header */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-rose-400" />
                      <span className="w-3 h-3 rounded-full bg-amber-400" />
                      <span className="w-3 h-3 rounded-full bg-emerald-400" />
                    </div>
                    <div className="flex-1 h-0.5 bg-white/60 rounded-full" />
                    <Server className={`w-4 h-4 ${f.accentClass} opacity-40`} />
                  </div>
                  {/* Stat display */}
                  <div className={`text-5xl font-display font-black mb-1 ${f.accentClass}`}>{f.stat}</div>
                  <div className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-6">{f.statLabel}</div>
                  {/* Progress bars */}
                  <div className="space-y-2">
                    <div className={`h-2 rounded-full bg-white/70`}>
                      <div className={`h-2 rounded-full ${f.iconBg} w-full opacity-70`} />
                    </div>
                    <div className={`h-2 rounded-full bg-white/70`}>
                      <div className={`h-2 rounded-full ${f.iconBg} w-4/5 opacity-50`} />
                    </div>
                    <div className={`h-2 rounded-full bg-white/70`}>
                      <div className={`h-2 rounded-full ${f.iconBg} w-3/5 opacity-30`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
